import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/common/ErrorBoundary';

// Pages
import LoginPage from './pages/LoginPage';
import PatientsPage from './pages/PatientsPage';
import ReportPage from './pages/ReportPage';
import ReportPreviewPage from './pages/ReportPreviewPage';
import VO2MaxReportPage from './pages/VO2MaxReportPage';
import VO2MaxReportPreviewPage from './pages/VO2MaxReportPreviewPage';
import SummaryReportPage from './pages/SummaryReportPage';
import SummaryReportPreviewPage from './pages/SummaryReportPreviewPage';
import StrengthReportPage from './pages/StrengthReportPage';
import StrengthReportPreviewPage from './pages/StrengthReportPreviewPage';
import AlzheimerReportPage from './pages/AlzheimerReportPage';
import AlzheimerReportPreviewPage from './pages/AlzheimerReportPreviewPage';
import FatLossReportPage from './pages/FatLossReportPage';
import FatLossReportPreviewPage from './pages/FatLossReportPreviewPage';
import LabResultsPage from './pages/LabResultsPage';
import NotFoundPage from './pages/NotFoundPage';
import DashboardPage from './pages/DashboardPage';
import HealthGoalsPage from './pages/HealthGoalsPage';
import TestMastersPage from './pages/TestMastersPage';
import VitalTestMastersPage from './pages/VitalTestMastersPage';
import BranchMastersPage from './pages/BranchMastersPage';
import TestCC4ParserPage from './pages/TestCC4ParserPage';
import CategoryScoreMasterPage from './pages/CategoryScoreMasterPage';
import TacticsMasterPage from './pages/TacticsMasterPage';
import HistoryQuestionsPage from './pages/HistoryQuestionsPage';
import PatientManagementPage from './pages/PatientManagementPage';
import ReportStaticTextsPage from './pages/ReportStaticTextsPage';
import AppointmentsPage from './pages/AppointmentsPage';

// Components
import Layout from './components/layout/Layout';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { PatientProvider } from './contexts/PatientContext';
import { TestMasterProvider } from './contexts/TestMasterContext';
import { VitalTestProvider } from './contexts/VitalTestContext';
import { BranchProvider } from './contexts/BranchContext';
import { ReportStaticTextsProvider } from './contexts/ReportStaticTextsContext';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <PatientProvider>
          <TestMasterProvider>
            <VitalTestProvider>
              <BranchProvider>
                <ReportStaticTextsProvider>
                  <ErrorBoundary>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/patients"
                element={
                  <ProtectedRoute>
                    <PatientsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/health-goals"
                element={
                  <ProtectedRoute>
                    <HealthGoalsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/appointments"
                element={
                  <ProtectedRoute>
                    <AppointmentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient-management"
                element={
                  <ProtectedRoute>
                    <PatientManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/:patientId"
                element={
                  <ProtectedRoute>
                    <ReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report/:patientId/preview"
                element={
                  <ProtectedRoute>
                    <ReportPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vo2max-report/:patientId"
                element={
                  <ProtectedRoute>
                    <VO2MaxReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vo2max-report/:patientId/preview"
                element={
                  <ProtectedRoute>
                    <VO2MaxReportPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/summary-report/:patientId"
                element={
                  <ProtectedRoute>
                    <SummaryReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/summary-report/:patientId/preview"
                element={
                  <ProtectedRoute>
                    <SummaryReportPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strength-report/:patientId"
                element={
                  <ProtectedRoute>
                    <StrengthReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/strength-report/:patientId/preview"
                element={
                  <ProtectedRoute>
                    <StrengthReportPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alzheimer-report/:patientId"
                element={
                  <ProtectedRoute>
                    <AlzheimerReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alzheimer-report/:patientId/preview"
                element={
                  <ProtectedRoute>
                    <AlzheimerReportPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fatloss-report/:patientId"
                element={
                  <ProtectedRoute>
                    <FatLossReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fatloss-report/:patientId/preview"
                element={
                  <ProtectedRoute>
                    <FatLossReportPreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/lab-results/:patientId"
                element={
                  <ProtectedRoute>
                    <LabResultsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-masters"
                element={
                  <ProtectedRoute>
                    <TestMastersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vital-test-masters"
                element={
                  <ProtectedRoute>
                    <VitalTestMastersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/branch-masters"
                element={
                  <ProtectedRoute>
                    <BranchMastersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/category-score-master"
                element={
                  <ProtectedRoute>
                    <CategoryScoreMasterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tactics-master"
                element={
                  <ProtectedRoute>
                    <TacticsMasterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/history-questions"
                element={
                  <ProtectedRoute>
                    <HistoryQuestionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/report-static-texts"
                element={
                  <ProtectedRoute>
                    <ReportStaticTextsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-cc4-parser"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TestCC4ParserPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
                  </ErrorBoundary>
                </ReportStaticTextsProvider>
              </BranchProvider>
            </VitalTestProvider>
          </TestMasterProvider>
        </PatientProvider>
      </AuthProvider>
      <ToastContainer position="top-right" autoClose={3000} />
    </Router>
  );
}

export default App;
